import {APP_BASE_HREF, PlatformLocation} from '@angular/common';
import {Inject, Injectable, Optional, DOCUMENT} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class UrlService {

  constructor(@Inject(DOCUMENT) private readonly document: Document,
              private readonly platformLocation: PlatformLocation,
              @Optional() @Inject(APP_BASE_HREF) private readonly appBaseHref: null | string) {
  }

  public buildUrl(url: string, ...params: string[]): string {
    const segments = url.split('/');
    const requiredParams = segments.reduce((counter, segment) => counter + (segment.startsWith(':') ? 1 : 0), 0);

    if (requiredParams !== params.length) {
      throw new Error('BuildUrl expected ' + requiredParams + ' parameters but received ' + params.length);
    }

    params.forEach(param => {
      if (!param) {
        throw new Error('BuildUrl parameter cannot be empty');
      }
    });

    let paramsIndex = 0;

    return segments
      .map(segment => segment.startsWith(':') ? encodeURIComponent(params[paramsIndex++]) : segment)
      .join('/');
  }

  public buildAbsoluteUrl(url: string, ...params: string[]): string {
    const relativeAppUrl = this.buildUrl(url, ...params);

    const origin = this.document.location?.origin;

    // origin should never be null, but just in case it is, throw an error
    if (!origin) {
      throw new Error('No origin available.');
    }

    // Use the injected APP_BASE_HREF by default
    let baseHref = this.appBaseHref;

    // If APP_BASE_HREF is not available, fall back to the <base href="..."> value in the DOM
    if (!baseHref) {
      baseHref = this.platformLocation.getBaseHrefFromDOM();
    }

    // By this point, baseHref should never be null, but just in case it is, throw an error
    if (!baseHref) {
      throw new Error('No base href set.');
    }

    const absoluteContextRoot = this.joinWithSlash(origin, baseHref);

    return this.joinWithSlash(absoluteContextRoot, relativeAppUrl);
  }

  private joinWithSlash(start: string, end: string): string {
    if (start.length === 0) {
      return end;
    }

    if (end.length === 0) {
      return start;
    }

    let slashes = 0;

    if (start.endsWith('/')) {
      slashes++;
    }

    if (end.startsWith('/')) {
      slashes++;
    }

    if (slashes === 2) {
      return start + end.substring(1);
    }

    if (slashes === 1) {
      return start + end;
    }

    return start + '/' + end;
  }
}
